import { useState } from 'react'

import { IconZoomIn } from '@tabler/icons-react'
import { Form, Image } from 'antd'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { PlatformAction } from '../types/blueprint'

import { DisplayInfoWrapper, SmallHeading } from './DisplayBlock.style'
import { getPlatformActionsId } from './utils/mapValuesToTemplate'

import { Tag } from '@/components/shared-components/BUI/Tag'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { useValidateActionAttachments } from '@/hooks/useValidateActionAttachments'
import {
  Field,
  FormFieldValues,
  useGetAction,
  useUpdateAction,
} from '@/modules/actions/actionHooks'
import { getEditInitialValues } from '@/modules/actions/actionsHelper'
import { ACTION_STATUS } from '@/modules/actions/constants'
import { UpdateActionDrawer } from '@/modules/actions/UpdateActionDrawer'
import { routes } from '@/utils/index'

const ACTION_ATTACHMENT_SIZE = 75
const ACTION_ATTACHMENT_PDF_SIZE = 120

const ActionWrapper = styled.div`
  padding: 0.25rem 0.25rem 0.25rem 0.5rem;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border-radius: ${({ theme }) => theme.borderRadius.small};

  display: flex;
  flex-direction: column;

  cursor: pointer;

  &:hover {
    box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.1);
  }

  ${({ theme: { media } }) => media.md`
    grid-template-columns: 1fr auto auto;
    grid-template-rows: auto;
  `}
`

const ActionContext = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;

  ${({ theme: { media } }) => media.md`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`

const ActionInfo = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  gap: 0.25rem;

  ${({ theme: { media } }) => media.md`
    flex-direction: row;
  `}
`

const ActionAttachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`

type FormProps = {
  parentId: string
  initialActions: PlatformAction[] | false
}

/**
 * Used when rendering the template in "capture" mode
 */
export const DisplayFormPlatformActions: React.FC<FormProps> = ({
  parentId,
  initialActions,
}) => {
  const platformActionsId = getPlatformActionsId(parentId)
  const fallback = initialActions === false ? [] : initialActions

  const shouldUpdate = (prev: object, curr: object) =>
    prev[platformActionsId] !== curr[platformActionsId]

  return (
    <Form.Item shouldUpdate={shouldUpdate} noStyle>
      {({ getFieldValue }) => {
        const actions: PlatformAction[] =
          getFieldValue(platformActionsId) || fallback

        return (
          <DisplayPlatformActions
            actions={actions}
            parentId={parentId}
            enableInlineEditing
          />
        )
      }}
    </Form.Item>
  )
}

type Props = {
  actions: PlatformAction[] | false
  parentId: string
  enableInlineEditing?: boolean
  renderingInPdf?: boolean
}

/**
 * Used directly when rendering the template in "display" mode
 */
export const DisplayPlatformActions: React.FC<Props> = ({
  actions = [],
  parentId,
  enableInlineEditing = false,
  renderingInPdf,
}) => {
  const router = useRouter()
  const [editActionId, setActionId] = useState<string | null>(null)

  const startEditing = (id: string) => setActionId(id)
  const stopEditing = () => setActionId(null)

  if (!actions || !actions?.length) return null

  const onItemClick = (id: string) => () => {
    enableInlineEditing
      ? startEditing(id)
      : router.push(routes.actions.edit(id))
  }

  return (
    <DisplayInfoWrapper>
      <SmallHeading>Actions</SmallHeading>

      <Flexbox flexDirection="column" gap="0.375rem">
        {actions.map((action) => (
          <ActionWrapper key={action.id} onClick={onItemClick(action.id)}>
            <ActionContext>
              <Text variant="label02">{action.title}</Text>
              <ActionInfo>
                <ActionAttachments>
                  <Image.PreviewGroup>
                    {action.attachments?.map((attachment) => (
                      <Image
                        onClick={(e) => e.stopPropagation()} // prevent routing or opening side drawer
                        key={attachment.id}
                        width={
                          renderingInPdf
                            ? ACTION_ATTACHMENT_PDF_SIZE
                            : ACTION_ATTACHMENT_SIZE
                        }
                        height={
                          renderingInPdf
                            ? ACTION_ATTACHMENT_PDF_SIZE
                            : ACTION_ATTACHMENT_SIZE
                        }
                        src={attachment.thumbnailUrl ?? attachment.url}
                        preview={{
                          src: attachment.url,
                          mask: <IconZoomIn size={16} />,
                        }}
                        alt={`action-attachment-${attachment.id}`}
                        style={{ objectFit: 'contain' }}
                        wrapperStyle={{
                          borderRadius: '0.25rem',
                          overflow: 'hidden',
                        }}
                      />
                    ))}
                  </Image.PreviewGroup>
                </ActionAttachments>
                <Tag
                  size="small"
                  variant={ACTION_STATUS[action.status].variant}
                  icon={ACTION_STATUS[action.status].icon}
                  label={ACTION_STATUS[action.status].label}
                />
              </ActionInfo>
            </ActionContext>
            <Text variant="caption01" secondary="_500">
              {action.description}
            </Text>
          </ActionWrapper>
        ))}
      </Flexbox>

      {editActionId && (
        <EditPlatformAction
          actionId={editActionId}
          stopEditing={stopEditing}
          blockId={parentId}
        />
      )}
    </DisplayInfoWrapper>
  )
}

type EditProps = {
  actionId: string
  blockId: string
  stopEditing: () => void
}

/**
 * Used to inline edit the action in the template
 */
export const EditPlatformAction: React.FC<EditProps> = ({
  actionId,
  blockId,
  stopEditing,
}) => {
  const { form, extraInfo, templateResponseId, builderId, config } =
    useTemplateRenderer()

  const resourceLinkId = extraInfo?.resourceLinkId as string

  const { action } = useGetAction(actionId, resourceLinkId)
  const { validateAttachmentRequired } = useValidateActionAttachments()
  const { updateAction, loading: updating } = useUpdateAction(
    templateResponseId,
    resourceLinkId
  )

  const handleSubmit = async (values: FormFieldValues) => {
    const valid = validateAttachmentRequired(
      values[Field.Status],
      values[Field.Attachments],
      action
    )

    if (!valid) return

    const updatedAction = await updateAction(actionId, values)

    const platformActionsId = getPlatformActionsId(blockId)
    const currentActions = form.getFieldValue(
      platformActionsId
    ) as PlatformAction[]

    const updatedActions = currentActions?.map((action) => {
      if (action.id === updatedAction.id) {
        return {
          id: updatedAction.id,
          title: updatedAction.title,
          status: updatedAction.status,
          attachments: updatedAction.attachments?.map((a) => ({
            id: a.id,
            url: a.url,
          })),
          description: updatedAction.description,
        }
      }
      return action
    }) as PlatformAction[]

    form.setFieldsValue({ [platformActionsId]: updatedActions })
    stopEditing()
  }

  return (
    <UpdateActionDrawer
      open
      onClose={stopEditing}
      actionId={action?.id}
      builderId={builderId}
      formProps={{
        initialValues: getEditInitialValues(action),
        onFinish: handleSubmit,
      }}
      isPublic={config?.isPublic}
      loading={updating}
      disableRequireAttachments={
        action?.__typename === 'PlatformAction' ? !action.isOwner : true
      }
    />
  )
}
